import React, {useState} from "react";
import {Button, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {useAsyncAction} from "nate-react-api-helpers";
import {Dialog2} from "../../../../misc/Dialog2";
import {api} from "../../../../api/API";
import {RenderingStatus} from "../../../../api/ElevationTool";

// SubmittalRenderGate warns before generating a submittal whose elevation renderings aren't all
// ready. "Render now & wait" prioritizes the project's outstanding renderings (retrying failures) and
// polls until nothing is pending; "Generate anyway" proceeds with placeholders for any not-yet-ready.
export function SubmittalRenderGate(props: {
    projectId: number;
    status: RenderingStatus;
    onProceed(): void;
    onCancel(): void;
}) {
    const [status, setStatus] = useState(props.status);

    const renderNow = useAsyncAction(async () => {
        await api.elevationTool.prioritizeRenderings({project: props.projectId});

        // poll until nothing is pending. A retried failure either succeeds (pending drops) or ends as
        // failed (not pending), so pending does reach 0 unless a render is genuinely stuck. Only
        // proceed once pending hits 0 (any remaining failures fall back to placeholders); on timeout,
        // surface the error and keep the dialog open rather than silently embedding placeholders.
        for(let i = 0; i < 150; i++) {
            const s = await api.elevationTool.renderingStatus({project: props.projectId});
            setStatus(s);
            if(s.pending === 0) {
                props.onProceed();
                return;
            }
            await sleep(2000);
        }

        throw new Error("Renderings are still processing after 5 minutes. Try again, or use “Generate anyway” to proceed with placeholders.");
    }, []);

    return (
        <Dialog2 open maxWidth="xs" fullWidth onClose={props.onCancel}>
            <DialogTitle>Elevation renderings not ready</DialogTitle>
            <DialogContent>
                <div>
                    {status.pending} pending{status.failed ? `, ${status.failed} failed` : ""}.
                </div>
                <div style={{marginTop: 8, color: "#666"}}>
                    Generating now embeds a “rendering in progress” placeholder for any elevation image
                    that isn’t ready yet.
                </div>
                {renderNow.LoadingElement}
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onCancel} disabled={renderNow.loading}>Cancel</Button>
                <Button onClick={props.onProceed} disabled={renderNow.loading}>Generate anyway</Button>
                <Button variant="contained" onClick={() => renderNow.callback(undefined)} disabled={renderNow.loading}>
                    Render now &amp; wait
                </Button>
            </DialogActions>
        </Dialog2>
    );
}

function sleep(ms: number) {
    return new Promise<void>(resolve => setTimeout(resolve, ms));
}
